/**
 * NeuralJourneyGraph — 3D force-directed temporal journey renderer.
 *
 * DRR-126 Phase 5: Renders N entity journeys as a living 3D graph using
 * react-force-graph-3d (Three.js/WebGL). Each entity = a thread of task-nodes
 * through time. Intersection nodes (shared care events) pulse with a highlight
 * color — the moments two lives touched.
 *
 * Entity color families (DRR-126):
 *   Residents/subjects  → Blue   (#3b82f6)
 *   Clinical staff      → Green  (#22c55e)
 *   Administrative      → Amber  (#f59e0b)
 *   Dietary/Kitchen     → Orange (#f97316)
 *   Housekeeping        → Purple (#a855f7)
 *   Regulatory          → Red    (#ef4444)
 *   Intersection nodes  → Yellow (#facc15)
 *
 * Data tier: T2. Backend enforces scope per DRR-102 + DRR-114.
 * No clinical content displayed — task metadata only.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import type { JourneyEdge, JourneyNode } from './hooks/useEntityJourneyGraph';

// ---------------------------------------------------------------------------
// Actor color families — DRR-126
// ---------------------------------------------------------------------------

export const ACTOR_TYPE_COLORS: Record<string, string> = {
  resident: '#3b82f6',   // Blue — subject/resident
  subject: '#3b82f6',
  clinical: '#22c55e',   // Green — DON, RN, LPN
  administrative: '#f59e0b', // Amber — ALD, coordinators
  dietary: '#f97316',    // Orange — kitchen/dietary
  housekeeping: '#a855f7', // Purple — housekeeping
  regulatory: '#ef4444', // Red — auditors, inspectors
  performer: '#94a3b8',  // Slate — generic performer
  default: '#64748b',
};

const INTERSECTION_COLOR = '#facc15'; // Yellow — shared event highlight
const TEMPORAL_EDGE_COLOR = 'rgba(148,163,184,0.3)';
const INTERSECTION_EDGE_COLOR = 'rgba(250,204,21,0.7)';

// ---------------------------------------------------------------------------
// Graph data types for react-force-graph-3d
// ---------------------------------------------------------------------------

interface GraphNode {
  id: string;
  label: string;
  color: string;
  val: number; // node size
  journeyNode: JourneyNode;
  isIntersection: boolean;
}

interface GraphLink {
  source: string;
  target: string;
  color: string;
  width: number;
  edgeType: string;
}

interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface NeuralJourneyGraphProps {
  /** Actor display names included in the graph (DRR-105: never raw sequential IDs) */
  actorNames: string[];
  /** Journey nodes from useEntityJourneyGraph */
  nodes: JourneyNode[];
  /** Journey edges from useEntityJourneyGraph */
  edges: JourneyEdge[];
  /** Set of instance IDs that are intersection points */
  intersectionInstanceIds: Set<string>;
  /** Map: actorName → color family key (e.g., "resident", "clinical") */
  actorTypeMap?: Record<string, string>;
  /** Loading state */
  loading?: boolean;
  /** Called when a node is clicked */
  onNodeClick?: (node: JourneyNode) => void;
  /** Height of the 3D viewport (default 600) */
  height?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NeuralJourneyGraph({
  actorNames,
  nodes,
  edges,
  intersectionInstanceIds,
  actorTypeMap = {},
  loading = false,
  onNodeClick,
  height = 600,
}: NeuralJourneyGraphProps) {
  const graphRef = useRef<unknown>(null);
  const [selectedNode, setSelectedNode] = useState<JourneyNode | null>(null);
  const [highlightedActors, setHighlightedActors] = useState<Set<string>>(new Set());

  // Build graph data for react-force-graph-3d
  const graphData = useMemo<GraphData>(() => {
    const graphNodes: GraphNode[] = nodes.map((n) => {
      const isIntersection = n.instanceId
        ? intersectionInstanceIds.has(n.instanceId)
        : false;

      const actorType = actorTypeMap[n.actorName] ?? n.actorRole;
      const baseColor = ACTOR_TYPE_COLORS[actorType] ?? ACTOR_TYPE_COLORS['default'] ?? '#64748b';
      const color = isIntersection ? INTERSECTION_COLOR : baseColor;

      return {
        id: n.nodeId,
        label: n.taskName,
        color,
        val: isIntersection ? 6 : 3,
        journeyNode: n,
        isIntersection,
      };
    });

    const graphLinks: GraphLink[] = edges.map((e) => ({
      source: e.sourceNodeId,
      target: e.targetNodeId,
      color:
        e.edgeType === 'intersection'
          ? INTERSECTION_EDGE_COLOR
          : TEMPORAL_EDGE_COLOR,
      width: e.edgeType === 'intersection' ? 2 : 1,
      edgeType: e.edgeType,
    }));

    return { nodes: graphNodes, links: graphLinks };
  }, [nodes, edges, intersectionInstanceIds, actorTypeMap]);

  const handleNodeClick = useCallback(
    (node: GraphNode) => {
      setSelectedNode(node.journeyNode);
      onNodeClick?.(node.journeyNode);
    },
    [onNodeClick],
  );

  const toggleActorHighlight = useCallback((actorId: string) => {
    setHighlightedActors((prev) => {
      const next = new Set(prev);
      if (next.has(actorId)) {
        next.delete(actorId);
      } else {
        next.add(actorId);
      }
      return next;
    });
  }, []);

  const intersectionCount = useMemo(
    () => nodes.filter((n) => n.instanceId && intersectionInstanceIds.has(n.instanceId)).length,
    [nodes, intersectionInstanceIds],
  );

  if (loading) {
    return (
      <Box style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stack align="center" gap="sm">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Building journey graph for {actorNames.length} actor{actorNames.length !== 1 ? 's' : ''}…
          </Text>
        </Stack>
      </Box>
    );
  }

  if (nodes.length === 0) {
    return (
      <Box style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Text size="sm" c="dimmed">
          No journey data found. Select actors with task history to visualize their journeys.
        </Text>
      </Box>
    );
  }

  return (
    <Stack gap="xs">
      {/* Toolbar */}
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs">
          <Title order={5}>Neural Journey Graph</Title>
          <Badge color="blue" variant="light">{nodes.length} nodes</Badge>
          <Badge color="yellow" variant="light">{intersectionCount} intersections</Badge>
          <Badge color="gray" variant="light">{edges.length} edges</Badge>
        </Group>
        <Group gap="xs">
          <Button
            size="xs"
            variant="subtle"
            onClick={() => setHighlightedActors(new Set())}
            disabled={highlightedActors.size === 0}
          >
            Reset filter
          </Button>
        </Group>
      </Group>

      {/* Actor filter chips — display names, never raw IDs (DRR-105) */}
      <Group gap="xs" wrap="wrap">
        {actorNames.map((actorName) => {
          const actorType = actorTypeMap[actorName] ?? 'default';
          const color = ACTOR_TYPE_COLORS[actorType] ?? ACTOR_TYPE_COLORS['default'];
          const isHighlighted =
            highlightedActors.size === 0 || highlightedActors.has(actorName);
          return (
            <Chip
              key={actorName}
              checked={isHighlighted}
              onChange={() => toggleActorHighlight(actorName)}
              size="xs"
              color="blue"
              style={{ opacity: isHighlighted ? 1 : 0.4 }}
            >
              <span style={{ color, fontWeight: 600 }}>●</span>{' '}
              {actorName}
            </Chip>
          );
        })}
      </Group>

      {/* 3D Graph viewport */}
      <Box
        style={{
          height,
          borderRadius: 8,
          overflow: 'hidden',
          background: '#0f172a',
          position: 'relative',
        }}
      >
        <ForceGraph3DRenderer
          graphRef={graphRef}
          graphData={graphData}
          height={height}
          highlightedActors={highlightedActors}
          onNodeClick={handleNodeClick}
        />

        {/* Legend overlay */}
        <Box
          style={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            background: 'rgba(15,23,42,0.85)',
            borderRadius: 6,
            padding: '8px 12px',
          }}
        >
          <Stack gap={4}>
            {Object.entries(ACTOR_TYPE_COLORS)
              .filter(([k]) => k !== 'default' && k !== 'performer')
              .map(([type, color]) => (
                <Group key={type} gap={6}>
                  <Box
                    style={{ width: 10, height: 10, borderRadius: '50%', background: color }}
                  />
                  <Text size="xs" c="white" style={{ textTransform: 'capitalize' }}>
                    {type}
                  </Text>
                </Group>
              ))}
            <Group gap={6}>
              <Box
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: INTERSECTION_COLOR,
                }}
              />
              <Text size="xs" c="yellow">
                Intersection (shared event)
              </Text>
            </Group>
          </Stack>
        </Box>
      </Box>

      {/* Selected node detail */}
      {selectedNode && (
        <Paper p="sm" withBorder>
          <Group justify="space-between">
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                {selectedNode.taskName}
              </Text>
              <Group gap="xs">
                <Badge size="xs" color="blue" variant="outline">
                  {selectedNode.taskType}
                </Badge>
                <Badge size="xs" color="gray" variant="outline">
                  {selectedNode.status}
                </Badge>
                <Badge size="xs" color="violet" variant="outline">
                  {selectedNode.actorRole}
                </Badge>
                {selectedNode.instanceId && intersectionInstanceIds.has(selectedNode.instanceId) && (
                  <Badge size="xs" color="yellow">
                    ⚡ Intersection
                  </Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed">
                {selectedNode.actorName} · {selectedNode.timestamp.slice(0, 10)}
              </Text>
            </Stack>
            <Button size="xs" variant="subtle" onClick={() => setSelectedNode(null)}>
              ✕
            </Button>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// ForceGraph3DRenderer — lazy-loads react-force-graph-3d to avoid SSR issues.
// Renders a placeholder canvas until the library loads.
// ---------------------------------------------------------------------------

interface RendererProps {
  graphRef: React.MutableRefObject<unknown>;
  graphData: GraphData;
  height: number;
  highlightedActors: Set<string>;
  onNodeClick: (node: GraphNode) => void;
}

function ForceGraph3DRenderer({
  graphRef,
  graphData,
  height,
  highlightedActors,
  onNodeClick,
}: RendererProps) {
  const [ForceGraph3D, setForceGraph3D] = React.useState<React.ComponentType<unknown> | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Standard dynamic import — Vite statically analyzes this string and bundles
    // react-force-graph-3d as a split chunk. The ts-ignore suppresses the TS module
    // resolution error (types declared in workflow-forms tsconfig paths are not needed
    // since we cast the result to ComponentType<unknown> below).
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: react-force-graph-3d is a direct dep, types resolved at runtime
    (import('react-force-graph-3d') as Promise<{ default?: React.ComponentType<unknown> }>)
      .then((mod: { default?: React.ComponentType<unknown> }) => {
        setForceGraph3D(() => (mod.default ?? mod) as React.ComponentType<unknown>);
      })
      .catch((err: Error) => {
        console.error('[NeuralJourneyGraph] react-force-graph-3d load failed:', err);
        setLoadError(err.message);
      });
  }, []);

  if (loadError) {
    return (
      <Box style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stack align="center" gap="xs">
          <Text size="sm" c="red">
            3D renderer unavailable: {loadError}
          </Text>
          <Text size="xs" c="dimmed">
            Run: pnpm add react-force-graph-3d
          </Text>
        </Stack>
      </Box>
    );
  }

  if (!ForceGraph3D) {
    return (
      <Box style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size="sm" color="blue" />
      </Box>
    );
  }

  // Filter nodes by highlighted actors (if any filter active)
  const filteredData =
    highlightedActors.size === 0
      ? graphData
      : {
          nodes: graphData.nodes.filter(
            (n) => highlightedActors.has(n.journeyNode.actorName),
          ),
          links: graphData.links.filter((l) => {
            const srcActor = (typeof l.source === 'object'
              ? (l.source as GraphNode).journeyNode?.actorName
              : '') ?? '';
            const tgtActor = (typeof l.target === 'object'
              ? (l.target as GraphNode).journeyNode?.actorName
              : '') ?? '';
            return highlightedActors.has(srcActor) || highlightedActors.has(tgtActor);
          }),
        };

  const G = ForceGraph3D as React.ComponentType<Record<string, unknown>>;

  return (
    <G
      ref={graphRef}
      graphData={filteredData}
      height={height}
      backgroundColor="#0f172a"
      nodeLabel={(node: GraphNode) =>
        `${node.journeyNode.taskName} (${node.journeyNode.actorName})`
      }
      nodeColor={(node: GraphNode) => node.color}
      nodeVal={(node: GraphNode) => node.val}
      linkColor={(link: GraphLink) => link.color}
      linkWidth={(link: GraphLink) => link.width}
      onNodeClick={(node: GraphNode) => onNodeClick(node)}
      nodeThreeObjectExtend={false}
    />
  );
}

export type { JourneyNode, JourneyEdge };
